import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '@/lib/utils'
import { tapScale } from '@/lib/motion'
import { HeroCards } from './HeroCards'
import { EmojiBar } from './ChatBubble'

interface Card {
  rank: string
  suit: string
}

type ActionState =
  | 'spectator-unauth'
  | 'spectator-auth'
  | 'seat-selection'
  | 'your-turn'
  | 'not-your-turn'
  | 'sitting-out'
  | 'raise'

interface ActionZoneProps {
  state: ActionState
  isCompact: boolean
  heroCards: Card[]
  handStrength?: string
  winAmount?: number | null
  // Turn actions
  currentBet?: number
  pot?: number
  playerStack?: number
  raiseAmount?: number
  onRaiseAmountChange?: (val: number) => void
  // Auto-actions
  autoFold?: boolean
  autoCheck?: boolean
  onToggleAutoFold?: () => void
  onToggleAutoCheck?: () => void
  // Callbacks
  onLogin?: () => void
  onSitDown?: () => void
  onCancelSitDown?: () => void
  onFold?: () => void
  onCheck?: () => void
  onCall?: () => void
  onRaise?: () => void
  onConfirmRaise?: () => void
  onCancelRaise?: () => void
  onImBack?: () => void
  onSendEmoji?: (emoji: string) => void
  onOpenChat?: () => void
  // Large raise confirm
  onLargeRaiseConfirm?: () => void
}

export function ActionZone({
  state,
  isCompact,
  heroCards,
  handStrength,
  winAmount,
  currentBet = 200,
  pot = 1800,
  playerStack = 12000,
  raiseAmount = 500,
  onRaiseAmountChange,
  autoFold = false,
  autoCheck = false,
  onToggleAutoFold,
  onToggleAutoCheck,
  onLogin,
  onSitDown,
  onCancelSitDown,
  onFold,
  onCheck,
  onCall,
  onRaise,
  onConfirmRaise,
  onCancelRaise,
  onImBack,
  onSendEmoji,
  onOpenChat,
}: ActionZoneProps) {
  const showHeroCards = state !== 'spectator-unauth' && state !== 'spectator-auth' && state !== 'seat-selection'

  return (
    <div className={cn(
      'relative flex items-center safe-area-bottom',
      isCompact ? 'h-[16%] min-h-[64px] px-2 gap-2' : 'h-[18%] min-h-[80px] px-3 gap-3'
    )}>
      {/* Subtle top edge */}
      <div className="absolute inset-x-0 top-0 h-px bg-white/[0.06]" />

      {/* Raise panel — expands upward from zone, positioned absolutely */}
      <AnimatePresence>
        {state === 'raise' && (
          <motion.div
            key="raise-overlay"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className={cn(
              'absolute left-0 right-0 bottom-0 z-10 safe-area-bottom',
              isCompact ? 'px-2 pb-2' : 'px-3 pb-3'
            )}
          >
            <div className={cn('glass-card-elevated rounded-2xl border border-white/10', isCompact ? 'p-2.5' : 'p-3')}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-muted-foreground">加注金額</span>
                <span className={cn('font-mono font-bold text-gold', isCompact ? 'text-base' : 'text-lg')}>${(raiseAmount / 100).toFixed(0)}</span>
              </div>
              <input
                type="range"
                min={currentBet * 2}
                max={playerStack}
                value={raiseAmount}
                onChange={(e) => onRaiseAmountChange?.(parseInt(e.target.value))}
                className="w-full h-2 bg-surface-elevated rounded-full appearance-none accent-gold mb-2"
              />
              <div className={cn('flex mb-2', isCompact ? 'gap-1' : 'gap-1.5')}>
                {[
                  { label: '½ 底池', val: Math.floor(pot / 2) },
                  { label: '底池', val: pot },
                  { label: '2x 底池', val: pot * 2 },
                  { label: 'All In', val: playerStack },
                ].map((preset) => (
                  <button
                    key={preset.label}
                    onClick={() => onRaiseAmountChange?.(Math.min(preset.val, playerStack))}
                    className={cn(
                      'flex-1 rounded-lg text-[10px] font-bold border transition-all',
                      isCompact ? 'py-1.5' : 'py-2',
                      raiseAmount === preset.val
                        ? 'bg-gold/20 border-gold/30 text-gold'
                        : 'bg-surface-elevated border-white/5 text-muted-foreground'
                    )}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
              <div className={cn('flex', isCompact ? 'gap-1.5' : 'gap-2')}>
                <button onClick={onCancelRaise} className={cn('flex-1 glass-card rounded-xl font-semibold text-muted-foreground', isCompact ? 'py-2 text-xs' : 'py-2.5 text-sm')}>
                  取消
                </button>
                <button onClick={onConfirmRaise} className={cn('flex-1 btn-primary rounded-xl font-bold', isCompact ? 'py-2 text-xs' : 'py-2.5 text-sm')}>
                  確認 ${(raiseAmount / 100).toFixed(0)}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hero cards area (~25%) */}
      <div className={cn('flex-shrink-0 flex items-center justify-center', isCompact ? 'w-[90px]' : 'w-[110px]')}>
        <HeroCards
          cards={heroCards}
          isCompact={isCompact}
          visible={showHeroCards && state !== 'raise'}
          handStrength={handStrength}
          winAmount={winAmount}
        />
      </div>

      {/* Action content (~75%) — hidden when raise overlay is active */}
      <div className={cn('flex-1 flex items-center justify-center min-w-0', state === 'raise' && 'invisible')}>
        <AnimatePresence mode="wait">
          {state === 'spectator-unauth' && (
            <motion.div key="unauth" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <motion.button
                whileTap={tapScale}
                onClick={onLogin}
                className={cn(
                  'btn-primary rounded-full font-bold tracking-wide shadow-xl shadow-gold/20',
                  isCompact ? 'h-9 px-6 text-xs' : 'h-11 px-8 text-sm'
                )}
              >
                登入即可加入遊戲
              </motion.button>
            </motion.div>
          )}

          {state === 'spectator-auth' && (
            <motion.div key="auth" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <motion.button
                whileTap={tapScale}
                onClick={onSitDown}
                className={cn(
                  'btn-primary rounded-full font-bold tracking-wide shadow-xl shadow-gold/20',
                  isCompact ? 'h-9 px-6 text-xs' : 'h-11 px-8 text-sm'
                )}
              >
                入座
              </motion.button>
            </motion.div>
          )}

          {state === 'seat-selection' && (
            <motion.div key="seat-sel" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
              className="flex flex-col items-center gap-2"
            >
              <p className={cn('text-foreground font-semibold', isCompact ? 'text-xs' : 'text-sm')}>選擇空位入座</p>
              <button
                onClick={onCancelSitDown}
                className={cn(
                  'bg-surface-elevated hover:bg-white/10 rounded-lg text-foreground font-semibold transition-colors border border-white/5',
                  isCompact ? 'px-4 py-1.5 text-xs' : 'px-5 py-2 text-sm'
                )}
              >
                取消
              </button>
            </motion.div>
          )}

          {state === 'your-turn' && (
            <motion.div key="turn" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
              className="w-full"
            >
              <div className={cn('grid grid-cols-4 w-full', isCompact ? 'gap-1.5' : 'gap-2')}>
                <motion.button whileTap={tapScale} onClick={onFold}
                  className={cn('rounded-xl bg-gradient-to-b from-rose-600/80 to-rose-700/80 border border-rose-500/30 text-white font-bold text-sm flex items-center justify-center backdrop-blur-md shadow-lg', isCompact ? 'h-11' : 'h-12')}
                >
                  棄牌
                </motion.button>
                <motion.button whileTap={tapScale} onClick={onCheck}
                  className={cn('rounded-xl glass-card text-foreground/80 font-bold text-sm hover:bg-white/10 flex items-center justify-center', isCompact ? 'h-11' : 'h-12')}
                >
                  過牌
                </motion.button>
                <motion.button whileTap={tapScale} onClick={onCall}
                  className={cn('rounded-xl bg-gradient-to-b from-gold/80 to-[hsl(var(--gold-deep))]/80 border border-gold/30 text-background font-bold text-sm flex flex-col items-center justify-center backdrop-blur-md shadow-lg', isCompact ? 'h-11' : 'h-12')}
                >
                  <span className="text-[10px] font-normal opacity-80">跟注</span>
                  <span>${currentBet / 100}</span>
                </motion.button>
                <motion.button whileTap={tapScale} onClick={onRaise}
                  className={cn('rounded-xl bg-gradient-to-b from-emerald-600 to-emerald-700 text-white font-bold text-sm flex flex-col items-center justify-center shadow-lg shadow-emerald-900/30', isCompact ? 'h-11' : 'h-12')}
                >
                  <span className="text-[10px] font-normal opacity-80">加注</span>
                  <span>至 $500</span>
                </motion.button>
              </div>
            </motion.div>
          )}

          {state === 'not-your-turn' && (
            <motion.div key="waiting" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="flex items-center gap-3"
            >
              {/* Auto-action toggles */}
              <div className="flex items-center gap-1.5">
                <button
                  onClick={onToggleAutoFold}
                  className={cn(
                    'px-2.5 py-1.5 rounded-lg text-[10px] font-bold border transition-all',
                    autoFold ? 'bg-rose-500/20 border-rose-500/30 text-rose-400' : 'glass-card text-muted-foreground border-white/10'
                  )}
                >
                  自動棄牌
                </button>
                <button
                  onClick={onToggleAutoCheck}
                  className={cn(
                    'px-2.5 py-1.5 rounded-lg text-[10px] font-bold border transition-all',
                    autoCheck ? 'bg-emerald-500/20 border-emerald-500/30 text-emerald-400' : 'glass-card text-muted-foreground border-white/10'
                  )}
                >
                  自動過牌
                </button>
              </div>

              {/* Emoji bar */}
              {onSendEmoji && onOpenChat && (
                <EmojiBar
                  onSendEmoji={onSendEmoji}
                  onOpenChat={onOpenChat}
                  isCompact={isCompact}
                />
              )}
            </motion.div>
          )}

          {state === 'sitting-out' && (
            <motion.div key="sitout" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              <motion.button
                whileTap={tapScale}
                onClick={onImBack}
                className={cn(
                  'rounded-2xl bg-gradient-to-r from-gold to-[hsl(var(--gold-deep))] text-background font-bold shadow-xl shadow-gold/30',
                  isCompact ? 'h-11 px-10 text-sm' : 'h-14 px-16 text-lg'
                )}
              >
                我回來了
              </motion.button>
            </motion.div>
          )}

          {/* raise state is handled by the absolute overlay above */}
        </AnimatePresence>
      </div>
    </div>
  )
}
